import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { login, forgotPassword } from '../redux/modules/user/actions';
import inactivityTimerIcon from '/icons/inactivity-timer.svg';
import CommonAuthComponent from './CommonAuthComponent';
import CustomInput from '../components/common/CustomInput';
import Button from '../components/common/Button';
import ConfirmationModalNew from '../components/common/ConfirmationModalNew';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [shouldShowWelcomeBack, setShouldShowWelcomeBack] = useState(false);
  const [showHelpText, setShowHelpText] = useState(false);


  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { loginLoading, loginError, requiresMfa, userDetailsLoading, isAccountLocked } = useSelector((state) => state.users);


  // Handle "Welcome back" logic based on login state and daily reset
  useEffect(() => {
    const today = new Date().toDateString(); // Gets date string like "Mon Dec 25 2023"
    const lastLoginDate = localStorage.getItem('lastLoginDate');
    const hasLoggedInToday = localStorage.getItem('hasLoggedInToday');
    const wasLoggedOutToday = localStorage.getItem('wasLoggedOutToday');

    // Check if user should see "Welcome back"
    // Conditions: user has logged in today OR was logged out today (manual or inactivity timeout)
    const shouldWelcomeBack = hasLoggedInToday === 'true' || wasLoggedOutToday === 'true';

    // Reset daily flags if it's a new day
    if (lastLoginDate !== today) {
      localStorage.removeItem('hasLoggedInToday');
      localStorage.removeItem('wasLoggedOutToday');
      localStorage.setItem('lastLoginDate', today);
      setShouldShowWelcomeBack(false);
    } else {
      setShouldShowWelcomeBack(shouldWelcomeBack);
    }
  }, []);

  // Mark user as logged in today when login is successful
  useEffect(() => {
    if (!loginLoading && !loginError && username && password) {
      const today = new Date().toDateString();
      localStorage.setItem('hasLoggedInToday', 'true');
      localStorage.setItem('lastLoginDate', today);
      localStorage.removeItem('wasLoggedOutToday'); // Clear logout flag on successful login
    }
  }, [loginLoading, loginError, username, password]);

  // Pre-fill username field when userId is available in URL and clean URL
  useEffect(() => {
    const userIdFromUrl = getUserIdFromUrl();
    if (userIdFromUrl && userIdFromUrl.length > 0) {
      setShowHelpText(true);
    }

    if (userIdFromUrl && !username) {
      setUsername(userIdFromUrl);

      // Clean the URL by removing the userId parameter for privacy
      const cleanUrl = new URL(window.location);
      cleanUrl.searchParams.delete('userId');

      // Replace current URL without userId parameter
      window.history.replaceState({}, '', cleanUrl.pathname + cleanUrl.search);
    }
  }, [username]);

  // Read isNewUser from query string to optionally hide Forgot Password for first-time sign-in
  const isNewUser = (() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('isNewUser') ?? '';
    const val = String(raw).toLowerCase();
    return ['true', '1', 'yes'].includes(val);
  })();

  // Extract userId from query parameters for pre-filling username
  const getUserIdFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get('userId');
  };

  const isMFAExpiredRedirect = (() => {
    const params = new URLSearchParams(location.search);
    return params.get('reason') === 'mfa-expired';
  })();

  // Check if user was redirected due to inactivity
  const isInactivityRedirect = (() => {
    const params = new URLSearchParams(location.search);
    return params.get('reason') === 'inactivity';
  })();

  useEffect(() => {
    if (requiresMfa) {
      navigate('/mfa-verification', { replace: true });
    }
  }, [requiresMfa, navigate]);

  // Handle 15-minute timeout for inactivity redirect
  useEffect(() => {
    let timeoutId;

    if (isInactivityRedirect || isMFAExpiredRedirect) {
      // Set a 15-minute timeout to remove the inactivity query parameter
      timeoutId = setTimeout(() => {
        // Mark user as logged out today (for welcome back logic)
        localStorage.setItem('wasLoggedOutToday', 'true');

        // Remove the reason=inactivity query parameter from URL
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.delete('reason');

        // Navigate to the clean URL without query parameters
        navigate(currentUrl.pathname, { replace: true });
      }, 15 * 60 * 1000); // 15 minutes in milliseconds
    }

    // Cleanup timeout on component unmount or when inactivity redirect changes
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isInactivityRedirect, isMFAExpiredRedirect, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (username && password) {
      // Mark user as having logged in before
      sessionStorage.setItem('hasLoggedInBefore', 'true');
      
      // Read hxReturnUrl from query params and save to sessionStorage if present
      const params = new URLSearchParams(location.search);
      const hxReturnUrl = params.get('hxReturnUrl');
      if (hxReturnUrl) {
        sessionStorage.setItem('hxReturnUrl', hxReturnUrl);
      }
      
      dispatch(login(username, password, navigate));
    }
  };

  return (
    <CommonAuthComponent>
      {/* Account Temporarily Locked Popup */}
      <ConfirmationModalNew
        open={isAccountLocked}
        title="Account Temporarily Locked"
        titleClassName="text-[22px]"
        content={() => (
          <div className="text-left">
            <p className="text-[16px] font-normal leading-[20px] text-gray-700 mb-4">
              For security reasons, your account has been temporarily locked after multiple unsuccessful login attempts.
            </p>
            <p className="text-[16px] font-normal leading-[20px] text-gray-700">
              Select Reset Password to regain access.
            </p>
          </div>
        )}
        onCross={() => dispatch({ type: 'CLEAR_ACCOUNT_LOCKED' })}
        confirmText={'Reset Password'}
        confirmAction={() => {
          dispatch({ type: 'CLEAR_ACCOUNT_LOCKED' });
          dispatch(forgotPassword(username, navigate, true));
        }}
        type="primary"
        hideActions={false}
        hideCloseIcon={false}
        cancelText="Try Again Later"
        handleClose={() => dispatch({ type: 'CLEAR_ACCOUNT_LOCKED' })}
        cancelBtnType="primary-outline"
        buttonclass = "!h-[52px]"
        scrollable={false}
        className="!w-[630px] !min-w-[630px] !max-w-[630px] !gap-4 "
      />

      {/* Adjusted spacing above logo */}
      <div className="flex flex-col justify-center items-center gap-6">
        {/* Conditional UI based on inactivity redirect */}
        {isInactivityRedirect ? (
          <div className="text-center flex flex-col items-center justify-center">
            <img
              src={inactivityTimerIcon}
              alt="Session Expired"
              className="h-[80px] w-[80px] mb-4"
            />
            <h1 className="text-xl text-teal-300 font-medium leading-[30px]">
              Your session has expired due to inactivity.
            </h1>
          </div>
        ) : (
          <div className="text-center">
            <h1 className="text-[22px] text-center font-medium leading-10">
              {userDetailsLoading ? 'Loading Your Profile' : shouldShowWelcomeBack ? 'Welcome Back' : 'Hello'}
            </h1>
          </div>
        )}
      </div>

      {!userDetailsLoading ? (
        <form onSubmit={handleSubmit}>
          {/* Username Input */}
          <CustomInput
            label="Email Address"
            name="username"
            className={`bg-white mt-[40px]  ${isNewUser ? 'mb-6' : 'mb-[6px]'}`}
            type={'text'}
            value={username}
            placeholder="Enter Email Address"
            onChange={(e) => setUsername(e.target.value)}
            hideHistory={true}
            // NewDesign={true}
            required
          />
          {!isNewUser && (
            <div className="mb-4 flex flex-col items-end space-y-1">
              <button
                type="button"
                onClick={() => navigate('/forgot-username')}
                className="text-teal-300 font-medium hover:font-bold underline"
              >
                Forgot Username?
              </button>
            </div>)}

          {/* Password Input with Toggle */}
          <div className="relative">
            <CustomInput
              label="Password"
              name="password"
              className={`bg-white ${isNewUser ? 'mb-6' : 'mb-[6px]'}`}
              type={'password'}
              value={password}
              placeholder="Enter Password"
              onChange={(e) => setPassword(e.target.value)}
              hideHistory={true}
              required
            />
          </div>

          {/* Forgot Password (right-aligned, under password) */}
          {!isNewUser && (
            <div className="flex justify-end mt-1 mb-4">
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="text-teal-300 font-medium hover:font-bold underline"
              >
                Forgot Password?
              </button>
            </div>
          )}

          {/* Error Message */}
          {loginError && (
            <div className="text-red-200 text-sm text-center bg-red-50 border border-red-100 p-2 my-4 rounded">
              {loginError}
            </div>
          )}

          {isMFAExpiredRedirect && (
            <div className='text-[16px] text-center mb-1 pt-4 px-4 text-teal-300'>
              Your session expired because verification wasn’t completed. Please, sign-in again.
            </div>
          )}

          {showHelpText && (
            <div className='text-[16px] text-center mb-1 pt-4 px-4 text-teal-300'>
              We filled in your username. Please enter your password to continue.
            </div>
          )}

          {/* Sign In Button */}
          <div className="pt-2">
            <Button
              type="submit"
              text={loginLoading ? 'Signing In...' : 'Sign In'}
              disabled={loginLoading}
              className="w-full !px-2.5 !py-3.5"
              onClick={() => handleSubmit}
            />
          </div>
        </form>
      ) : (
        <div className="flex justify-center items-center h-32">
          <svg
            className="animate-spin text-reel-teal-600"
            width="48"
            height="48"
            viewBox="0 0 38 38"
            xmlns="http://www.w3.org/2000/svg"
            stroke="currentColor"
          >
            <g fill="none" fillRule="evenodd">
              <g transform="translate(1 1)" strokeWidth="2">
                <circle strokeOpacity=".3" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 18 18"
                    to="360 18 18"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            </g>
          </svg>
        </div>
      )}
    </CommonAuthComponent>
  );
};

export default Login;
